import { useCallback, useEffect, useState } from 'react';
import { css } from '@emotion/react';
import { Person } from '@weave/schema-gen-ts/dist/schemas/persons/v3/persons.pb';
import { ContactType_Enum } from '@weave/schema-gen-ts/dist/shared/persons/v3/enums.pb';
import { PaymentMethod, PaymentPlanFrequency, setDefaultPaymentMethod } from '@frontend/api-payment-plans';
import { getTodaysDate } from '@frontend/date';
import { useTranslation } from '@frontend/i18n';
import { PatientDropdown, personFullName } from '@frontend/patient-dropdown';
import { PaymentMethodItem, usePaymentPlanCardOnFile } from '@frontend/payments-card-on-file';
import { useMultiLocationUtils } from '@frontend/payments-hooks';
import { useChargesEnabledLocations } from '@frontend/person-invoice-create';
import { breakpoints } from '@frontend/responsiveness';
import { useAppFlagStore } from '@frontend/shared';
import { useNavigate } from '@frontend/weave-router';
import { theme } from '@frontend/theme';
import {
  MoneyField,
  Text,
  useForm,
  TextareaField,
  DatePickerField,
  DropdownField,
  CreditCardIcon,
  useModalControl,
  Modal,
  TextLink,
  TextField,
  EmailField,
  FormRow,
  NumberField,
  useAlert,
  Button,
  BannerNotification,
} from '@frontend/design-system';
import { convertToCents } from '../../utils/currency';
import { termOptions } from '../../utils/date-helpers';
import { CreatePlanDetailsModal } from '../PaymentPlanModals/CreatePlanDetailsModal';
import { EditBillingMethodModal } from '../PaymentPlanModals/EditBillingMethodModal';
import { CreatePlanSummary } from './plan-summary';

const styles = {
  billingMethodInfo: (personId: string | undefined) => css`
    display: flex;
    flex-flow: row;
    border: 1px solid ${!personId ? theme.colors.neutral20 : theme.colors.neutral30};
    border-radius: ${theme.borderRadius.small};
    height: 80px;
    padding: ${theme.spacing(1, 1, 1, 2)};
    align-items: center;
    gap: ${theme.spacing(2)};
  `,
  formButtonContainer: css`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: ${theme.spacing(2)};
    @media (min-width: ${breakpoints.small.min}px) {
      flex-direction: row;
    }
  `,
  formButtonRow: css`
    display: flex;
    flex-direction: row;
    @media (max-width: ${breakpoints.small.min}px) {
      justify-content: space-evenly;
      width: 100%;
    }
    @media (min-width: ${breakpoints.small.min}px) {
      gap: ${theme.spacing(2)};
    }
  `,
  formButton: css`
    align-self: start;
    width: max-content;
  `,
  paymentMethodContainer: css`
    display: flex;
    flex-flow: row;
    width: 100%;
    justify-content: space-between;
  `,
  cardBrand: css`
    display: flex;
    flex-flow: row;
    align-items: center;
    gap: ${theme.spacing(2)};
  `,
  cardInformation: css`
    display: flex;
    flex: row;
    gap: ${theme.spacing(1)};
  `,
  changeButton: css`
    margin-right: ${theme.spacing(2)};
  `,
};

const BASE_TRACKING_ID = 'pay-portal-paymentplanscreate';

export const PaymentPlansCreate = () => {
  const { t } = useTranslation('payments');
  const alert = useAlert();
  const navigate = useNavigate();
  const { getLocationName } = useMultiLocationUtils();

  const {
    disabledMessage,
    chargesDisabled,
    chargesEnabledLocations,
    initialLocationId,
    hasManySelectedLocations,
    disabledMessageWithLink,
  } = useChargesEnabledLocations();

  const { modalProps: detailsModalProps, triggerProps: detailsTriggerProps } = useModalControl();
  const { modalProps: editModalProps, triggerProps: editTriggerProps } = useModalControl();
  const [selectedPatient, setSelectedPatient] = useState<Person | null>(null);
  const [patientPaymentMethod, setPatientPaymentMethod] = useState<PaymentMethod>();

  const { getFeatureFlagValue } = useAppFlagStore();
  const allowEmptyBillingMethod = getFeatureFlagValue({
    flagName: 'payments:payment-plans:allow-empty-billing',
    strategy: 'ANY',
    locationIds: chargesEnabledLocations,
  });

  const { getFieldProps, reset, values, isComplete, seedValues } = useForm({
    fields: {
      planName: {
        type: 'text',
        required: true,
      },
      locationId: {
        type: 'dropdown',
        required: true,
        value: initialLocationId,
      },
      personNameSearch: {
        type: 'text',
        required: true,
        value: '',
      },
      customerEmailId: {
        type: 'email',
        value: '',
        required: true,
      },
      frequency: {
        type: 'dropdown',
        value: PaymentPlanFrequency.Monthly,
        required: true,
      },
      paymentTerm: {
        type: 'number',
        value: '12',
        required: true,
        min: 1,
        max: 60,
      },
      planAmount: {
        type: 'money',
        min: 0.5,
        required: true,
      },
      paymentDate: {
        type: 'datePicker',
        required: true,
        value: getTodaysDate('MM/DD/YYYY'),
      },
      memo: {
        type: 'text',
      },
    },
  });

  const handleSetBillingMethod = async (selectedPaymentMethod: PaymentMethod) => {
    try {
      await setDefaultPaymentMethod(selectedPaymentMethod?.id, values.locationId ?? '');
      setPatientPaymentMethod(selectedPaymentMethod);
      editModalProps.onClose();
      alert.success(t('Payment method successfully updated.'));
    } catch (err) {
      alert.error(t('Unable to save payment method, please try again.'));
    }
  };

  const handleRemovePaymentMethod = () => {
    setPatientPaymentMethod(undefined);
  };

  const getPersonEmail = (person: Person | null): string => {
    return person?.contactInfo?.find((info) => info.type === ContactType_Enum.EMAIL)?.destination ?? '';
  };

  const handleSelectPatient = (patient: Person | null) => {
    setSelectedPatient(patient);
    seedValues({
      personNameSearch: patient ? personFullName(patient) : '',
      customerEmailId: getPersonEmail(patient),
    });
    setPatientPaymentMethod(undefined);
  };

  useEffect(() => {
    seedValues({ locationId: initialLocationId });
  }, [initialLocationId]);

  //reset patient details when location changes
  useEffect(() => {
    handleSelectPatient(null);
  }, [values.locationId]);

  const getTermPayment = () => Number(values?.planAmount) / Number(values?.paymentTerm) || 0;

  const { editBillingMethodProps, paymentMethodItemProps } = usePaymentPlanCardOnFile({
    locationId: values.locationId,
    paymentAmount: convertToCents(getTermPayment()),
    patientId: selectedPatient?.personId,
    patientEmail: getPersonEmail(selectedPatient),
    setSelectedPM: setPatientPaymentMethod,
    selectDefaultPaymentMethod: !allowEmptyBillingMethod,
    paymentMethodId: patientPaymentMethod?.id,
    paymentFrequency: values.frequency as PaymentPlanFrequency,
  });

  const onPlanCreateComplete = useCallback(() => {
    alert.success(t('Payment plan successfully created!'));
    navigate({
      to: '/payments/payment-plans',
      search: { updateList: true },
    });
  }, []);

  const onPlanCreateFailure = useCallback(() => {
    alert.error(t('Error creating plan, please try again.'));
  }, []);

  const disableSelectedACHPaymentMethod = patientPaymentMethod?.type === 'us_bank_account';

  const billingMethodIsValid = allowEmptyBillingMethod || !!patientPaymentMethod?.id;

  const previewDisabled =
    !selectedPatient || !isComplete || disableSelectedACHPaymentMethod || !billingMethodIsValid || chargesDisabled;

  return (
    <>
      {disabledMessageWithLink && (
        <BannerNotification
          css={css`
            margin-bottom: ${theme.spacing(2)};
          `}
          status='error'
        >
          {disabledMessageWithLink}
        </BannerNotification>
      )}
      <div
        css={css`
          display: flex;
          flex-flow: column;
          gap: ${theme.spacing(3)};
        `}
      >
        <Text>
          {t(
            'Fill out the payment plan details below. The customer will be notified when the payment plan is created.'
          )}
        </Text>
        <TextField
          data-trackingid={`${BASE_TRACKING_ID}-input-planname`}
          {...getFieldProps('planName')}
          label={t('Payment Plan Name *')}
        />
        {hasManySelectedLocations && (
          <DropdownField
            data-trackingid={`${BASE_TRACKING_ID}-locationId`}
            label={t('Location')}
            {...getFieldProps('locationId')}
          >
            {chargesEnabledLocations.map((locationId) => (
              <DropdownField.Option key={locationId} value={locationId}>
                {getLocationName(locationId)}
              </DropdownField.Option>
            ))}
          </DropdownField>
        )}
        <PatientDropdown
          trackingId={`${BASE_TRACKING_ID}-input-patient`}
          setSelectedPatient={handleSelectPatient}
          searchFieldProps={getFieldProps('personNameSearch')}
          disabled={!values.locationId}
          disabledMessage={disabledMessage || t('Location must be selected first')}
          locationId={values.locationId}
        />
        <MoneyField
          data-trackingid={`${BASE_TRACKING_ID}-input-planamount`}
          {...getFieldProps('planAmount')}
          label={t('Total Amount *')}
        />
        <FormRow
          css={css`
            margin-bottom: 0;
          `}
        >
          <DropdownField
            data-trackingid={`${BASE_TRACKING_ID}-input-frequency`}
            label={t('Payment cadence *')}
            {...getFieldProps('frequency')}
          >
            {termOptions.map((term) => (
              <DropdownField.Option key={term.value} value={term.value}>
                {term.displayValue}
              </DropdownField.Option>
            ))}
          </DropdownField>
          <NumberField
            data-trackingid={`${BASE_TRACKING_ID}-input-paymentterm`}
            label={t('Number of payments *')}
            {...getFieldProps('paymentTerm')}
          />
        </FormRow>
        <DatePickerField
          data-trackingid={`${BASE_TRACKING_ID}-input-paymentdate`}
          {...getFieldProps('paymentDate')}
          label={t('Date of first payment *')}
        />
        {patientPaymentMethod?.id ? (
          <>
            <PaymentMethodItem
              trackingId={BASE_TRACKING_ID}
              paymentMethod={patientPaymentMethod}
              onChange={editTriggerProps.onClick}
              showRemoveButton={allowEmptyBillingMethod}
              onRemove={handleRemovePaymentMethod}
              showError
              {...paymentMethodItemProps}
            />
          </>
        ) : (
          <div css={styles.billingMethodInfo(selectedPatient?.personId)}>
            <CreditCardIcon size={24} color={!selectedPatient?.personId ? 'disabled' : 'default'} />
            {!selectedPatient?.personId ? (
              <Text color='disabled'>{t('Billing Method') + (!allowEmptyBillingMethod ? '*' : '')}</Text>
            ) : (
              <TextLink data-trackingid={`${BASE_TRACKING_ID}-btn-addbillingmethod`} {...editTriggerProps}>
                {t('Add Billing Method') + (!allowEmptyBillingMethod ? '*' : '')}
              </TextLink>
            )}
          </div>
        )}

        <TextareaField
          data-trackingid={`${BASE_TRACKING_ID}-input-memo`}
          {...getFieldProps('memo')}
          label={t('Internal Note')}
        />
        <div>
          <Text css={{ marginBottom: theme.spacing(2) }}>Send confirmation email to</Text>
          <EmailField
            data-trackingid={`${BASE_TRACKING_ID}-input-customeremailid`}
            {...getFieldProps('customerEmailId')}
            label={t('Email *')}
          />
        </div>
        {values.planAmount && (
          <CreatePlanSummary
            amount={getTermPayment()}
            frequency={values?.frequency as PaymentPlanFrequency}
            date={values?.paymentDate}
          />
        )}
        <div css={styles.formButtonContainer}>
          <div css={styles.formButtonRow}>
            <Button
              variant='secondary'
              data-trackingid={`${BASE_TRACKING_ID}-btn-reset`}
              onClick={() => reset()}
              css={styles.formButton}
            >
              {t('Clear Form')}
            </Button>
            <Button
              data-trackingid={`${BASE_TRACKING_ID}-btn-preview`}
              {...detailsTriggerProps}
              css={styles.formButton}
              disabled={previewDisabled}
            >
              {t('Preview')}
            </Button>
          </div>
          {previewDisabled && (
            <Text color='light' weight='light' size='small'>
              {t('Complete all required fields')}
            </Text>
          )}
        </div>
      </div>
      <Modal {...detailsModalProps} maxWidth={600}>
        <CreatePlanDetailsModal
          onClose={detailsModalProps.onClose}
          onComplete={onPlanCreateComplete}
          onFailure={onPlanCreateFailure}
          planDetails={
            values.locationId
              ? {
                  locationId: values.locationId,
                  planName: values.planName,
                  planAmount: convertToCents(Number(values.planAmount)),
                  patient: selectedPatient
                    ? {
                        id: selectedPatient.personId,
                        firstName: selectedPatient.firstName || '',
                        lastName: selectedPatient.lastName || '',
                      }
                    : undefined,
                  customerEmailId: values.customerEmailId,
                  frequency: values.frequency as PaymentPlanFrequency,
                  paymentTerm: values.paymentTerm,
                  paymentDate: values.paymentDate,
                  termAmount: convertToCents(getTermPayment()),
                  billingMethod: patientPaymentMethod,
                  memo: values.memo,
                }
              : undefined
          }
        />
      </Modal>
      <Modal {...editModalProps} minWidth={600} maxWidth={600}>
        <EditBillingMethodModal
          handleSetBillingMethod={handleSetBillingMethod}
          onClose={editModalProps.onClose}
          {...editBillingMethodProps}
        />
      </Modal>
    </>
  );
};
export default PaymentPlansCreate;
